import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import ModalBody from 'Components/Common/Modal/ModalBody';
import ModalContainer from 'Components/Common/Modal/ModalContainer';
import ModalFooter from 'Components/Common/Modal/ModalFooter';
import ModalHeader from 'Components/Common/Modal/ModalHeader';
import PromoteTable from './PromoteTable';
import { promoteModel } from '../ARSServices/ARSServices';
import { toast } from 'react-toastify';
import { ArsContext } from '../Contexts/ArsContext';
import { RecommenderLogsResponse } from '../Types';

type TPromoteRecommenderModelProps = {
    closePromoteModal: () => void
    showModal: boolean;
    selectedItem: RecommenderLogsResponse[];
    channelKey: string;
    setSelectedItems: React.Dispatch<React.SetStateAction<RecommenderLogsResponse[]>>
    getRecLogs: () => Promise<void>
}

const PromoteRecommenderModel = ({ showModal, closePromoteModal, selectedItem, channelKey, setSelectedItems, getRecLogs }: TPromoteRecommenderModelProps) => {
    const { checkPromoteeStatus } = useContext(ArsContext);
    const [selectedApplets, setSelectedApplets] = useState<AppletPayload>({});
    const [localShowModal, setLocalShowModal] = useState<boolean>(false);
    const [isPromoting, setIsPromoting] = useState<boolean>(false);
    const [changesMade, setChangesMade] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [appletsInABTesting, setAppletsInABTesting] = useState<string[]>([]);
    const [liveModelsData, setLiveModelsData] = useState<LiveModelApiResponse | null>(null);

    const isPromotingRef = useRef<boolean>(false);

    useEffect(() => {
        setLocalShowModal(showModal);
    }, [showModal]);

    useEffect(() => {
        if (channelKey) {
            updateSelectedAppletsWithEnvAndChannel(channelKey);
        }
    }, [channelKey]);

    const updateSelectedAppletsWithEnvAndChannel = (channel: string) => {
        setSelectedApplets((prevState) => {
            const { channel: __, ...appletEntries } = prevState;
            return {
                ...appletEntries,
                channel
            } as AppletPayload;
        });
    };

    const promoteArs = async () => {
        try {
            setIsPromoting(true);
            isPromotingRef.current = true;
            const response = await promoteModel(selectedApplets);
            if (response?.done) {
                checkPromoteeStatus(response?.job_id);
                getRecLogs();
            } else {
                toast.error(response?.message);
            }
        } catch (error) {
            console.error("promoteArs ~ error:", error);
            toast.error("An error occurred while promoting the model");
        } finally {
            setIsPromoting(false);
            isPromotingRef.current = false;
            setSelectedItems([]);
            handleCloseModal();
        }
    }

    const handleCloseAttempt = useCallback(() => {
        if (isPromotingRef.current || isPromoting) {
            return;
        }
        handleCloseModal();
    }, [isPromoting]);

    const handleChangesMade = useCallback((changes: boolean) => {
        setChangesMade(changes);
    }, []);

    const handleCloseModal = useCallback(() => {
        setLocalShowModal(false);
        closePromoteModal();
    }, [closePromoteModal]);

    const createInitialSelectedApplets = (liveModelsData: LiveModelApiResponse, channelKey: string): AppletPayload => {
        const appletEntries = Object.entries(liveModelsData).reduce((acc, [key, appletData]) => {
            if (appletData && typeof appletData === 'object') {
                acc[key] = {
                    name: appletData.name ?? '',
                    ab_testing_enabled: appletData.ab_testing_enabled ?? false,
                    models: appletData.models ?? [],
                    selectedModelId: appletData.models?.length > 0 ? appletData.models[0].id : ''
                };
            }
            return acc;
        }, {} as AppletEntries);

        const additionalProps: AdditionalProps = {
            channel: channelKey
        };

        return { ...appletEntries, ...additionalProps } as AppletPayload;
    };

    const hasLiveModelReplacement = useCallback(() => {
        if (!liveModelsData || !selectedApplets) return false;
        const initialState = createInitialSelectedApplets(liveModelsData, channelKey);
        const appletKeys = Object.keys(liveModelsData).filter(key =>
            !liveModelsData[key]?.ab_testing_enabled
        );
        return appletKeys.some(appletKey => {
            const liveApplet = liveModelsData[appletKey];
            if (!liveApplet || !liveApplet.models || liveApplet.models.length === 0) {
                return false;
            }
            const initialModelId = initialState[appletKey]?.selectedModelId || '';
            const currentModelId = selectedApplets[appletKey]?.selectedModelId || '';
            const liveModelId = liveApplet.models[0]?.id || '';
            return initialModelId !== currentModelId &&
                currentModelId !== '' &&
                currentModelId !== liveModelId;
        });
    }, [liveModelsData, selectedApplets, channelKey, createInitialSelectedApplets]);

    return (
        <>
            <ModalContainer isOpen={localShowModal} setOpen={handleCloseAttempt}>
                <ModalHeader>
                    <label className='tw-text-sm tw-font-semibold'>Promote Model</label>
                </ModalHeader>
                <ModalBody className='tw-w-[800px]'>
                    <PromoteTable selectedItems={selectedItem} selectedApplets={selectedApplets} setSelectedApplets={setSelectedApplets} channelKey={channelKey} onChangesMade={handleChangesMade} loading={loading} setLoading={setLoading} setAppletsInABTesting={setAppletsInABTesting} liveModelsData={liveModelsData} setLiveModelsData={setLiveModelsData} createInitialSelectedApplets={createInitialSelectedApplets} />
                </ModalBody>
                <ModalFooter className='p-0'>
                    <div>
                        <div className='tw-flex tw-items-center tw-justify-end tw-gap-4 tw-px-6 tw-py-3'>
                            <button onClick={handleCloseAttempt} className='outline-button' disabled={isPromoting}>Cancel</button>
                            <button
                                disabled={!changesMade || isPromoting || loading}
                                onClick={promoteArs}
                                className='button-style'
                            >
                                {isPromoting ? 'Promoting...' : 'Promote'}
                            </button>
                        </div>
                        {(!loading && changesMade && hasLiveModelReplacement()) ? (
                            <div className="tw-flex tw-items-center tw-justify-center tw-w-full tw-h-[41px] tw-bg-[#f4f5f7] tw-rounded tw-shadow tw-border">
                                <label className='tw-text-[#172B4D] tw-text-sm '>
                                    *You have selected new model(s) to replace the live model(s) in the associated Applet(s).
                                </label>
                            </div>
                        ) : null}
                        {appletsInABTesting?.length ? <div className="tw-flex tw-items-center tw-justify-center tw-w-full tw-h-[41px] tw-bg-[#f4f5f7] tw-rounded tw-shadow tw-border">
                            <label className='tw-text-[#172B4D] tw-text-sm'>
                                <span>*Models cannot be selected for the</span>
                                {appletsInABTesting?.map((x, index) => (
                                    <span key={index}> ‘{x}’,</span>
                                ))}
                                <span> due to A/B testing is in progress.</span>
                            </label>
                        </div> : null}
                    </div>
                </ModalFooter>
            </ModalContainer>
        </>
    );
}

export default PromoteRecommenderModel;